import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { 
  User, 
  Mail, 
  Building, 
  Briefcase, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Lock,
  Phone,
  MapPin,
  Globe,
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  Loader,
  RefreshCw
} from "lucide-react";
import NavbarComptable from '../components/NavbarComptable';
import SidebarComptable from '../components/SidebarComptable';

const ModifierClients = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    entreprise: "",
    secteur: "",
    registre_commerce: "",
    password: "",
    is_active: true
  });

  // Charger les données du client à modifier
  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await axios.get(
          `https://api-ec.cinef-expertises.com/api/clients/${id}`
        );
        const clientData = response.data;
        
        setFormData({
          nom: clientData.nom || "",
          prenom: clientData.prenom || "",
          email: clientData.email || "",
          entreprise: clientData.entreprise || "",
          secteur: clientData.secteur || "",
          registre_commerce: clientData.registre_commerce || "",
          password: "",
          is_active: clientData.is_active !== undefined ? clientData.is_active : true
        });
        
        setSelectedClient(clientData);
        setLoading(false);
      } catch (err) {
        setError("Erreur lors du chargement des données du client");
        setLoading(false);
        showNotification("Impossible de charger les données du client", "error");
      }
    };

    if (id) {
      fetchClient();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError("");
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
    setResetPassword(true);
    showNotification("Nouveau mot de passe généré", "success");
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      const updateData = {
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        entreprise: formData.entreprise,
        secteur: formData.secteur,
        registre_commerce: formData.registre_commerce,
        is_active: formData.is_active
      };

      // Ajouter le mot de passe seulement s'il a été modifié
      if (resetPassword && formData.password) {
        updateData.password = formData.password;
      }

      const response = await axios.put(
        `https://api-ec.cinef-expertises.com/api/clients/${id}`,
        updateData
      );

      showNotification("Client modifié avec succès !", "success");
      
      // Redirection après modification
      setTimeout(() => {
        navigate("/liste-clients");
      }, 2000);
      
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || "Une erreur est survenue");
      } else {
        setError("Erreur de connexion au serveur");
      }
      showNotification(err.response?.data?.message || "Erreur lors de la modification", "error");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <SidebarComptable
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <div className="main-wrapper">
          <NavbarComptable
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
          />

          <main className="main-content">
            <div className="loading-container">
              <div className="loading-spinner">
                <Loader className="spin" size={48} />
              </div>
              <p>Chargement des données du client...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="app-container">
        <SidebarComptable
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <div className="main-wrapper">
          <NavbarComptable
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
          />

          <main className="main-content">
            {/* Notification */}
            {notification && (
              <div className={`notification ${notification.type}`}>
                {notification.type === 'success' ? 
                  <CheckCircle size={20} /> : 
                  <AlertCircle size={20} />
                }
                <span>{notification.message}</span>
              </div>
            )}

            <div className="content-header">
              <button 
                onClick={() => navigate("/liste-clients")} 
                className="btn-back"
              >
                <ArrowLeft size={20} />
                <span>Retour à la liste</span>
              </button>
              
              <div className="header-content">
                <h1>Modifier le client</h1>
                <p>Mettre à jour les informations du client</p>
              </div>
            </div>

            <div className="form-container">
              <form onSubmit={handleSubmit} className="create-client-form">
                <div className="form-sections">
                  {/* Section Informations personnelles */}
                  <div className="form-section">
                    <h3>
                      <User size={20} />
                      Informations personnelles
                    </h3>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="prenom">
                          <User size={16} />
                          Prénom *
                        </label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          placeholder="Ex: Jean"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="nom">
                          <User size={16} />
                          Nom *
                        </label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          placeholder="Ex: Dupont"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">
                          <Mail size={16} />
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="client@exemple.com"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section Entreprise */}
                  <div className="form-section">
                    <h3>
                      <Building size={20} />
                      Informations entreprise
                    </h3>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="entreprise">
                          <Building size={16} />
                          Nom de l'entreprise
                        </label>
                        <input
                          type="text"
                          id="entreprise"
                          name="entreprise"
                          value={formData.entreprise}
                          onChange={handleChange}
                          placeholder="Ex: SARL Dupont"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="secteur">
                          <Briefcase size={16} />
                          Secteur d'activité
                        </label>
                        <select
                          id="secteur"
                          name="secteur"
                          value={formData.secteur}
                          onChange={handleChange}
                        >
                          <option value="">Sélectionner un secteur</option>
                          <option value={formData.secteur}>{formData.secteur}</option>
                          <option value="Agriculture">Agriculture</option>
                          <option value="Industrie">Industrie</option>
                          <option value="Construction">Construction</option>
                          <option value="Commerce">Commerce</option>
                          <option value="Transport">Transport</option>
                          <option value="Hébergement">Hébergement et restauration</option>
                          <option value="Information">Information et communication</option>
                          <option value="Finance">Activités financières</option>
                          <option value="Immobilier">Activités immobilières</option>
                          <option value="Services">Services aux entreprises</option>
                          <option value="Administration">Administration publique</option>
                          <option value="Enseignement">Enseignement</option>
                          <option value="Sante">Santé et action sociale</option>
                          <option value="Autres">Autres services</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="registre_commerce">
                          <FileText size={16} />
                          Registre du commerce
                        </label>
                        <input
                          type="text"
                          id="registre_commerce"
                          name="registre_commerce"
                          value={formData.registre_commerce}
                          onChange={handleChange}
                          placeholder="Ex: RB/658/ASS"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section Mot de passe */}
                  <div className="form-section">
                    <h3>
                      <Lock size={20} />
                      Mot de passe
                    </h3>
                    
                    <div className="password-section">
                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={resetPassword}
                            onChange={(e) => {
                              setResetPassword(e.target.checked);
                              if (!e.target.checked) {
                                setFormData(prev => ({ ...prev, password: "" }));
                              }
                            }}
                          />
                          Réinitialiser le mot de passe
                        </label>
                      </div>

                      {resetPassword && (
                        <>
                          <div className="form-row">
                            <div className="form-group password-input">
                              <label htmlFor="password">
                                <Lock size={16} />
                                Nouveau mot de passe
                              </label>
                              <div className="input-group">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  id="password"
                                  name="password"
                                  value={formData.password}
                                  onChange={handleChange}
                                  placeholder="Nouveau mot de passe"
                                />
                                <button
                                  type="button"
                                  className="btn-toggle-password"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                              </div>
                            </div>
                          </div>

                          <button 
                            type="button" 
                            onClick={generatePassword}
                            className="btn-generate"
                          >
                            <RefreshCw size={16} />
                            Générer un mot de passe sécurisé
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Section Statut */}
                  <div className="form-section">
                    <h3>
                      <CheckCircle size={20} />
                      Statut du compte
                    </h3>
                    
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                        />
                        <span className="checkmark"></span>
                        Compte actif
                      </label>
                      <p className="help-text">
                        Un compte inactif ne peut pas se connecter à la plateforme
                      </p>
                    </div>
                  </div>


                </div>

                {/* Messages d'erreur */}
                {error && (
                  <div className="error-message">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                  </div>
                )}

                {/* Boutons d'action */}
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={() => navigate("/liste-clients")}
                    className="btn-cancel"
                    disabled={saving}
                  >
                    <ArrowLeft size={16} />
                    Annuler
                  </button>
                  
                  <button 
                    type="submit" 
                    className="btn-submit"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader className="spin" size={16} />
                        Modification...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Modifier le client
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </main>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f8fafb 0%, #f1f5f9 100%);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .main-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .main-content {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
          background: linear-gradient(135deg, #f8fafb 0%, #f1f5f9 100%);
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
        }

        .loading-spinner {
          margin-bottom: 16px;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .notification {
          position: fixed;
          top: 24px;
          right: 24px;
          padding: 16px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-weight: 500;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          animation: slideIn 0.3s ease-out;
        }

        .notification.success {
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
        }

        .notification.error {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .content-header {
          display: flex;
          align-items: center;
          gap: 24px;
          margin-bottom: 32px;
        }

        .btn-back {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          color: #6b7280;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .btn-back:hover {
          background: #f9fafb;
          border-color: #d1d5db;
          color: #374151;
        }

        .header-content h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 4px;
          margin-left: 8px;
        }

        .header-content p {
          color: #6b7280;
          font-size: 1rem;
        }

        .form-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .create-client-form {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          border: 1px solid #f1f5f9;
        }

        .form-sections {
          display: flex;
          flex-direction: column;
          gap: 32px;
          margin-bottom: 32px;
        }

        .form-section {
          padding: 24px;
          background: #fafbfc;
          border-radius: 16px;
          border: 1px solid #e5e7eb;
        }

        .form-section h3 {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 24px;
          padding-bottom: 12px;
          border-bottom: 2px solid #e5e7eb;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          color: #374151;
          font-size: 0.95rem;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.2s ease;
          background: white;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #9ca3af;
        }

        .password-section {
          background: white;
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        .input-group {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-group input {
          flex: 1;
          padding-right: 50px;
        }

        .btn-toggle-password {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .btn-toggle-password:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-generate {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 12px;
        }

        .btn-generate:hover {
          background: linear-gradient(135deg, #7c3aed, #6d28d9);
          transform: translateY(-1px);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          font-weight: 500;
          color: #374151;
        }

        .checkbox-label input[type="checkbox"] {
          width: 20px;
          height: 20px;
          accent-color: #3b82f6;
        }

        .help-text {
          font-size: 0.875rem;
          color: #6b7280;
          margin-top: 4px;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          color: #dc2626;
          font-weight: 500;
          margin-bottom: 24px;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          padding-top: 24px;
          border-top: 2px solid #f3f4f6;
        }

        .btn-cancel,
        .btn-submit {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 24px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 2px solid transparent;
        }

        .btn-cancel {
          background: white;
          color: #6b7280;
          border-color: #e5e7eb;
        }

        .btn-cancel:hover {
          background: #f9fafb;
          border-color: #d1d5db;
          color: #374151;
        }

        .btn-submit {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          min-width: 180px;
          justify-content: center;
        }

        .btn-submit:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.25);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .content-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .create-client-form {
            padding: 24px 20px;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .btn-cancel,
          .btn-submit {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
};

export default ModifierClients;
